import React, { useEffect, useState } from 'react'

import { useHistory, Link } from 'react-router-dom'
import styled from 'styled-components'

import { useAdminZoneContext } from '../../../../contexts/adminZoneContext'
import { media } from '../../../../styles/breakpoints'
import { blockMobileFloorMapNavigation } from '../../../../utils/mobileFloorMapAvailability'

const ViewModeButton = styled.div`
  font-size: 14px;
  min-width: 90px;
  width: 100%;
  color: #a7aabd;
  text-align: center;
  padding: 6px 11px;
  font-weight: 400;
  flex-shrink: 0;

  @media ${media.belowTablet} {
    font-size: 13px;
    min-width: 44px;
    min-height: 44px;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:not(last-child) {
    margin-right: 10px;
  }

  cursor: pointer;
  user-select: none;

  &.active {
    font-weight: 700;
    color: #f8f8f8;
    background-color: #073c7a;

    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    border-radius: 2px;
  }
`

const TabBadge = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  background: ${props => (props.$active ? '#ffffff' : '#ef7e46')};
  color: ${props => (props.$active ? '#073c7a' : '#ffffff')};
`
ViewModeButton.displayName = 'ViewModeButton'

const ViewModeMenuScroll = styled.div`
  display: flex;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  max-width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }
`

interface LinkedViewModeButtonProps {
  viewName: string
  path: string
  active: boolean
  disabled: boolean
  blockMobileNavigation?: boolean
  badge?: number
}
const LinkedViewModeButton = ({
  viewName,
  path,
  active,
  disabled,
  blockMobileNavigation = false,
  badge,
}: LinkedViewModeButtonProps) => {
  const label = (
    <>
      {viewName}
      {!!badge && <TabBadge $active={active}>{badge}</TabBadge>}
    </>
  )
  if (disabled) {
    return (
      <ViewModeButton className={active ? 'active' : ''}>
        {label}
      </ViewModeButton>
    )
  }
  return (
    <Link
      to={path}
      onClick={event => {
        if (blockMobileNavigation && blockMobileFloorMapNavigation()) {
          event.preventDefault()
        }
      }}
    >
      <ViewModeButton className={active ? 'active' : ''}>
        {label}
      </ViewModeButton>
    </Link>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper StringMap value type
type StringMap = Record<string, any>

export interface ViewModeMenuProps {
  items: StringMap
  matchQueryParams?: boolean
  containerStyle?: React.CSSProperties
  zIndex?: number
}

export const ViewModeMenu = ({
  items,
  matchQueryParams = false,
  zIndex = 10,
  containerStyle = {},
}: ViewModeMenuProps) => {
  const history = useHistory()
  const location = history.location
  const fullPath = location.pathname + location.search
  const { inAdminZone } = useAdminZoneContext()
  const [activeItem, setActiveItem] = useState<string>(
    inAdminZone ? 'Schedule' : 'Site View',
  )

  /* Listen for route changes */
  useEffect(() => {
    const pathToMatch = matchQueryParams ? fullPath : location.pathname
    const searchParams = new URLSearchParams(location.search)
    const categoryParam = searchParams.get('category')

    if (matchQueryParams && categoryParam) {
      const metricKey = Object.keys(items).find(
        (key: string) => items[key].categorySlug === categoryParam,
      )
      if (metricKey) {
        setActiveItem(metricKey)
        return
      }
    }

    if (matchQueryParams && !categoryParam) {
      const safetyOverviewKey = Object.keys(items).find(
        (key: string) =>
          items[key].categorySlug == null &&
          (items[key].route === pathToMatch ||
            items[key].additionalActivePaths?.some(
              (path: string) => path === pathToMatch,
            ) ||
            (items[key].route &&
              location.pathname.startsWith(items[key].route.split('?')[0]))),
      )
      if (safetyOverviewKey) {
        setActiveItem(safetyOverviewKey)
        return
      }
    }

    const viewMode = Object.keys(items).find((key: string) => {
      const routeMatchesCurrentPathName = items[key].route === pathToMatch
      const pathNameInActivePaths =
        items[key].additionalActivePaths &&
        items[key].additionalActivePaths.find(
          (path: string) => path === pathToMatch,
        )

      return routeMatchesCurrentPathName || pathNameInActivePaths
    })
    if (viewMode) setActiveItem(viewMode)
  }, [fullPath, items, location.pathname, location.search, matchQueryParams])

  return (
    <ViewModeMenuScroll
      id="view-mode-menu-container"
      style={{ ...containerStyle, zIndex: zIndex }}
    >
      {Object.keys(items)
        .filter(item => items[item].display)
        .filter(item => items[item].userCanAccess)
        .map(item => (
          <LinkedViewModeButton
            viewName={item}
            path={items[item].route}
            disabled={!items[item].userCanAccess}
            blockMobileNavigation={items[item].blockMobileNavigation}
            badge={items[item].badge}
            active={item === activeItem}
            key={item}
          />
        ))}
    </ViewModeMenuScroll>
  )
}
